import React, { lazy, Suspense, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { PublicLayout } from 'common/layouts/Public/PublicLayout';
import { ErrorScreen, ForbiddenErrorScreen, InternalErrorScreen } from 'common/pages/ErrorPages';
import { DotsLoader } from 'components/common/DotsLoader';
import { Flex } from 'components/styled/Flex';
import { ACCESS_TOKEN } from 'consts/auth';
import { isAdriagate } from 'modules/_common';
import { TokenLogin } from 'modules/account/features/login/routes/TokenLogin';
import { ConfirmActivationRenter } from 'modules/account/features/signupRenter/routes/ConfirmActivationRenter';
import {
  ForgotPassword,
  Login,
  PasswordInvite,
  ResetPassword,
  SignUp,
  SignUpAdriagate,
  SignUpSuccess,
} from 'modules/account/routes';
import { LOGIN, useAuthRoutes } from 'routes/useAuthRoutes';
import { useAuthState } from 'services/auth/AuthContext';

const loadPrivateRoutes = () =>
  import(/* webpackChunkName: "private", webpackPrefetch: true */ 'routes/PrivateRoutes');
const PrivateRoutes = lazy(loadPrivateRoutes);

if (window.localStorage.getItem(ACCESS_TOKEN)) {
  loadPrivateRoutes();
}

const publicRoutes = [
  { path: 'login', element: <Login /> },
  { path: 'sign-up', element: <SignUp /> },
  { path: 'forgot-password', element: <ForgotPassword /> },
  { path: 'reset-password/:token', element: <ResetPassword /> },
  { path: 'set-password-invite/:token', element: <PasswordInvite /> },
];

const AppRoutes = ({ profileInfo }) => {
  const { isAuthenticated, email, checkPrivate, checkRegistration } = useAuthRoutes();
  const { logout } = useAuthState();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location?.pathname?.includes('set-password-invite')) {
      logout();
      navigate(location.pathname);
    }
  }, [location.pathname]);

  return (
    <Routes>
      {/* Adriagate registration */}
      {isAdriagate && <Route path="sign-up-renter/:customerId" element={<SignUpAdriagate />} />}

      <Route element={<PublicLayout />}>
        {/* Public routes */}

        {!isAuthenticated &&
          publicRoutes.map(({ path, element }, key) => (
            <Route key={key} path={path} element={element} />
          ))}

        <Route
          path="sign-up-success"
          element={checkRegistration(<SignUpSuccess email={email} />)}
        />

        <Route path="renter-anonymous-login" element={<ConfirmActivationRenter />} />
        <Route path="token-login" element={<TokenLogin />} />

        {/* error pages */}
        <Route path="403" element={<ForbiddenErrorScreen />} />
        <Route path="404" element={<ErrorScreen />} />
        <Route path="500" element={<InternalErrorScreen />} />
      </Route>

      {/* Private routes */}
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Suspense
              fallback={
                <Flex alignItems="center" justifyContent="center" height="100vh">
                  <DotsLoader />
                </Flex>
              }
            >
              <PrivateRoutes profileInfo={profileInfo} checkPrivate={checkPrivate} />
            </Suspense>
          ) : (
            <Navigate to={LOGIN} replace />
          )
        }
      />
    </Routes>
  );
};

export default withTranslation()(AppRoutes);
